import React from 'react';
import { Button } from '@vaiu/ui/button';
import { CheckCircle } from 'lucide-react';
import { roundMonetaryValue } from '@vaiu/utils/currency';

interface InvoicePaymentReceivedProps {
  invoiceCode: string;
  paymentAmount: number;
  currency: string;
  transactionFee?: number;
  newBalance: number;
  onContinue: () => void;
  message?: string;
  redeemer?: string;
  remainder?: number;
  status?: 'completed' | 'partial' | 'pending' | 'failed';
  displayMode?: 'fullscreen' | 'inline'; // New prop for display mode
}

const InvoicePaymentReceived: React.FC<InvoicePaymentReceivedProps> = ({
  invoiceCode,
  paymentAmount,
  currency,
  transactionFee = 0,
  newBalance,
  onContinue,
  message,
  redeemer,
  remainder,
  status = 'completed',
  displayMode = 'fullscreen'
}) => {
  const leftover = Number(remainder);
  const isPartial = status === 'partial' || (Number.isFinite(leftover) && leftover > 0);
  // Debug logging for amount values
  console.log('🔍 InvoicePaymentReceived received props:', {
    invoiceCode,
    paymentAmount,
    currency,
    transactionFee,
    newBalance,
    paymentAmountType: typeof paymentAmount,
    transactionFeeType: typeof transactionFee,
    newBalanceType: typeof newBalance
  });

  const formatCurrency = (amount: number) => {
    console.log('💰 formatCurrency called with:', { amount, currency, amountType: typeof amount });
    // Round first to prevent floating point precision issues (e.g., 1.000003, 8.9999999)
    const roundedAmount = roundMonetaryValue(amount, 2);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(roundedAmount);
    console.log('💰 formatCurrency result:', formatted);
    return formatted;
  };

  // Inline mode for showing within accordion
  if (displayMode === 'inline') {
    return (
      <div className="space-y-3 animate-in slide-in-from-bottom duration-300">
        {/* Success Alert */}
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-green-800">
                {isPartial ? 'Partial Payment Successful' : 'Invoice Paid Successfully'}
              </h3>
              <p className="text-green-700 text-sm mt-1">
                Invoice <span className="font-mono font-semibold">{invoiceCode}</span> has been {isPartial ? 'partially' : 'fully'} paid
              </p>
              {isPartial && remainder !== undefined && (
                <p className="text-orange-600 text-sm mt-2 font-medium">
                  ⚠️ Remaining balance: {formatCurrency(remainder)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Payment Details</h4>
          
          <div className="space-y-2">
            {/* Payment Amount */}
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Amount Paid:</span>
              <span className="font-bold text-green-600">
                {formatCurrency(paymentAmount)}
              </span>
            </div>

            {/* Transaction Fee */}
            {transactionFee > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Processing Fee:</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(transactionFee)}
                </span>
              </div>
            )}

            {/* Divider */}
            {transactionFee > 0 && <hr className="my-2 border-gray-200" />}

            {/* Total */}
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Total Deducted:</span>
              <span className="font-bold text-gray-900">
                {formatCurrency(paymentAmount + transactionFee)}
              </span>
            </div>

            {/* New Balance */}
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-gray-600">Your New Balance:</span>
              <span className="font-bold text-blue-600">
                {formatCurrency(newBalance)}
              </span>
            </div>
          </div>

          {/* Redeemer Info */}
          {redeemer && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-gray-500 text-sm">
                <span className="font-medium">Paid to:</span> {redeemer}
              </p>
            </div>
          )}

          {/* Message */}
          {message && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
              {message}
            </div>
          )}
        </div>

        {/* Action Button */}
        <Button 
          onClick={onContinue}
          className="w-full"
          variant="outline"
        >
          Done
        </Button>
      </div>
    );
  }

  // Full screen mode (original display)
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-1 bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-3">
        
        {/* Success Header */}
        <div className="mb-3 p-2 bg-green-50 border-2 border-green-200 rounded-lg text-center">
          <div className="flex justify-center mb-2">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-green-800 mb-2">
            {isPartial ? 'Partial Payment Successful' : 'Invoice Payment Successful'}
          </h2>
          <p className="text-green-700 text-sm">
            Invoice <span className="font-semibold">{invoiceCode}</span> has been {isPartial ? 'partially' : 'fully'} paid
          </p>
          {isPartial && remainder !== undefined && (
            <p className="text-orange-600 text-sm mt-1">
              Remaining balance: {formatCurrency(remainder)}
            </p>
          )}
        </div>

        {/* Payment Details */}
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 text-center">
            Payment Details
          </h3>
          
          {/* Payment Amount */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600 font-medium">Payment Amount:</span>
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(paymentAmount)}
            </span>
          </div>

          {/* Transaction Fee */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-500 text-sm">Processing Fee:</span>
            <span className="text-sm text-red-600">
              {formatCurrency(transactionFee)}
            </span>
          </div>

          <hr className="my-2 border-gray-300" />

          {/* Total Paid */}
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600 font-medium">Total Paid:</span>
            <span className="text-lg font-bold text-green-600">
              {formatCurrency(paymentAmount + transactionFee)}
            </span>
          </div>
        </div>

        {/* New Balance */}
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg text-center">
          <span className="text-blue-800">
            Your new balance: <span className="font-bold">{formatCurrency(newBalance)}</span>
          </span>
        </div>

        {/* Success Icon */}
        <div className="mb-3 flex justify-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
        </div>

        {/* Message */}
        {message && (
          <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
            <p className="text-yellow-800 text-sm">{message}</p>
          </div>
        )}

        {/* Redeemer Info */}
        {redeemer && (
          <div className="mb-2 text-center">
            <p className="text-gray-500 text-xs">
              <span className="font-medium">Paid to:</span> {redeemer}
            </p>
          </div>
        )}

        {/* Additional Info */}
        <div className="mb-3 text-center">
          <p className="text-gray-500 text-sm">
            Payment has been processed and your balance has been updated.
          </p>
        </div>

        {/* Continue Button */}
        <Button 
          onClick={onContinue}
          className="w-full bg-gray-800 hover:bg-gray-900 text-white py-3 text-lg"
        >
          Back to Home
        </Button>
      </div>
    </div>
  );
};

export default InvoicePaymentReceived; 